/**
 * Global loading overlay.
 *
 * Usage from anywhere:
 *   GlobalLoader.show();                                   // spinner only
 *   GlobalLoader.show('Uploading your design...');         // single message
 *   GlobalLoader.show(['Uploading...', 'Almost done...']); // rotating messages
 *   GlobalLoader.show(texts, { interval: 3000, loop: false });
 *   GlobalLoader.setText('Adding to cart...');             // swap copy while open
 *   GlobalLoader.hide();
 */
(function () {
  if (window.GlobalLoader) return;

  const DEFAULT_INTERVAL = 2500;

  class GlobalLoaderController {
    constructor() {
      this.overlay = null;
      this.textEl = null;
      this.texts = [];
      this.index = 0;
      this.timer = null;
      this.visible = false;
      this.previousBodyOverflow = '';
    }

    build() {
      if (this.overlay) return this.overlay;

      const overlay = document.createElement('div');
      overlay.className = 'global-loader';
      overlay.setAttribute('role', 'alert');
      overlay.setAttribute('aria-live', 'assertive');
      overlay.setAttribute('aria-hidden', 'true');
      overlay.innerHTML = `
        <div class="global-loader__box">
          <span class="global-loader__spinner-holder" aria-hidden="true">
            <span class="global-loader__spinner"></span>
          </span>
          <p class="global-loader__text"></p>
        </div>
      `;

      document.body.appendChild(overlay);

      this.overlay = overlay;
      this.textEl = overlay.querySelector('.global-loader__text');
      return overlay;
    }

    normalizeTexts(text) {
      if (text == null || text === '') return [];
      const list = Array.isArray(text) ? text : [text];
      return list.map((item) => String(item)).filter((item) => item.length > 0);
    }

    show(text, options = {}) {
      const overlay = this.build();

      this.texts = this.normalizeTexts(text);
      this.index = 0;
      this.interval = Number(options.interval) > 0 ? Number(options.interval) : DEFAULT_INTERVAL;
      this.loop = options.loop !== false;

      this.renderText();
      this.startRotation();

      if (!this.visible) {
        this.previousBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        this.visible = true;
      }

      overlay.setAttribute('aria-hidden', 'false');
      // Force a reflow so the fade-in transition runs even when show() is
      // called in the same tick as build().
      void overlay.offsetWidth;
      overlay.classList.add('is-visible');

      return this;
    }

    /** Replaces the copy without closing the overlay. */
    setText(text, options = {}) {
      this.texts = this.normalizeTexts(text);
      this.index = 0;
      if (Number(options.interval) > 0) this.interval = Number(options.interval);
      if (options.loop !== undefined) this.loop = options.loop !== false;

      this.renderText();
      this.startRotation();
      return this;
    }

    renderText() {
      if (!this.textEl) return;
      const current = this.texts[this.index] || '';
      this.textEl.textContent = current;
      this.textEl.style.display = current ? '' : 'none';
    }

    startRotation() {
      this.stopRotation();
      if (this.texts.length < 2) return;

      this.timer = setInterval(() => {
        const next = this.index + 1;

        if (next >= this.texts.length) {
          if (!this.loop) {
            this.stopRotation();
            return;
          }
          this.index = 0;
        } else {
          this.index = next;
        }

        this.fadeToCurrentText();
      }, this.interval);
    }

    fadeToCurrentText() {
      if (!this.textEl) return;
      this.textEl.classList.add('is-fading');
      setTimeout(() => {
        this.renderText();
        this.textEl.classList.remove('is-fading');
      }, 200);
    }

    stopRotation() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }

    hide() {
      this.stopRotation();
      if (!this.visible) return this;

      this.visible = false;
      document.body.style.overflow = this.previousBodyOverflow;

      if (this.overlay) {
        this.overlay.classList.remove('is-visible');
        this.overlay.setAttribute('aria-hidden', 'true');
      }

      return this;
    }

    get isVisible() {
      return this.visible;
    }
  }

  const controller = new GlobalLoaderController();

  // A loader left open while navigating away (checkout, for example) would come
  // back still spinning when the browser restores the page from its back/forward
  // cache, since that restores the DOM as-is. Close it on both ends of the trip.
  window.addEventListener('pagehide', () => controller.hide());
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) controller.hide();
  });

  window.GlobalLoader = {
    show: (text, options) => controller.show(text, options),
    setText: (text, options) => controller.setText(text, options),
    hide: () => controller.hide(),
    get isVisible() {
      return controller.isVisible;
    }
  };
})();
